import Phaser from 'phaser';
import { BootScene } from '@/scenes/BootScene';
import { SplashScene } from '@/scenes/SplashScene';
import { TitleScene } from '@/scenes/TitleScene';
import { MapSelectScene } from '@/scenes/MapSelectScene';
import { LevelTreeScene } from '@/scenes/LevelTreeScene';
import { GameScene } from '@/scenes/GameScene';
import { UIScene } from '@/scenes/UIScene';
import { WorkshopScene } from '@/scenes/WorkshopScene';
import { MapEditorScene } from '@/scenes/MapEditorScene';
import { PowerCurveScene } from '@/scenes/PowerCurveScene';
import { WorkshopGodScene } from '@/scenes/WorkshopGodScene';
import { GodModeScene } from '@/scenes/GodModeScene';
import { RobotLabScene } from '@/scenes/RobotLabScene';
import { EnemyTuningScene } from '@/scenes/EnemyTuningScene';
import { BalanceChartScene } from '@/scenes/BalanceChartScene';
import { CinematicEditorScene } from '@/cinematic/editor/CinematicEditorScene';
import { AnimationPreviewScene } from '@/scenes/AnimationPreviewScene';
import { CANVAS_WIDTH, CANVAS_HEIGHT } from '@/config/GameConfig';
import { SaveManager } from '@/systems/SaveManager';
import { SoundManager } from '@/systems/SoundManager';
import { CRTPipeline } from '@/systems/CRTPipeline';
import { SlowmoFXPipeline } from '@/systems/SlowmoFXPipeline';
import { installDevConsoleHotkey, setGameRef } from '@/systems/DevConsole';

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.WEBGL,
  width: CANVAS_WIDTH,
  height: CANVAS_HEIGHT,
  parent: 'game-container',
  backgroundColor: '#1a1a2e',
  fps: { target: 60, forceSetTimeOut: false },
  pixelArt: true,       // crisp pixel rendering (no blurry interpolation when zoomed)
  roundPixels: true,     // snap to integer pixels (reduces shimmer/aliasing)
  dom: { createContainer: true },  // enable DOM overlay for HTML inputs (email subscribe form)
  scene: [BootScene, SplashScene, TitleScene, MapSelectScene, LevelTreeScene, GameScene, UIScene, WorkshopScene, MapEditorScene, PowerCurveScene, WorkshopGodScene, GodModeScene, RobotLabScene, EnemyTuningScene, BalanceChartScene, CinematicEditorScene, AnimationPreviewScene],
  physics: {
    default: 'arcade',
    arcade: { debug: false },
  },
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    expandParent: true,
  },
};

const game = new Phaser.Game(config);

// Expose for playtest agent + Playwright QA
(window as any).__GAME__ = game;

// Initialize sound system (loads music, connects to EventBus)
SoundManager.init();

// Dev console: Cmd+Shift+C to open terminal overlay
setGameRef(game);
installDevConsoleHotkey();

// CRT post-processing: register pipeline once, auto-apply to every scene's main camera.
// UIScene is a HUD overlay — skip CRT to avoid double-processing.
const CRT_SKIP_SCENES = new Set(['UIScene', 'BootScene', 'SplashScene']);

game.events.on('ready', () => {
  const renderer = game.renderer;
  if (renderer instanceof Phaser.Renderer.WebGL.WebGLRenderer) {
    renderer.pipelines.addPostPipeline('CRTPipeline', CRTPipeline);
    renderer.pipelines.addPostPipeline('SlowmoFXPipeline', SlowmoFXPipeline);
  }
  // Hook into every scene's create event to auto-apply CRT if enabled
  for (const scene of game.scene.scenes) {
    scene.events.on('create', () => {
      if (CRT_SKIP_SCENES.has(scene.scene.key)) return;
      if (SaveManager.getSettings().crtEnabled) {
        scene.cameras.main?.setPostPipeline(CRTPipeline);
      }
    });
  }
});

// Fullscreen is only toggled via Options menu — do not auto-apply on load
// (browsers require a user gesture, and auto-triggering on first click hijacks that click)

// Disable browser right-click context menu on the game canvas so Phaser receives right-click events
game.canvas.addEventListener('contextmenu', (e) => e.preventDefault());

// Prevent browser back/forward navigation on horizontal swipe (especially on mobile/trackpad)
game.canvas.style.touchAction = 'none';
game.canvas.addEventListener('touchstart', (e) => e.preventDefault(), { passive: false });
